import { useState, useEffect, useRef } from "react";
import { Sun, Moon } from "lucide-react";

const ThemeToggle = () => {
  // SSG hydration safety: the initial render MUST match what the server
  // produced. Server returned `true` (default dark). We do the same on
  // first client render, then a mount-time useEffect reconciles to the
  // persisted localStorage value. The inline script in index.html has
  // already applied the right `html.dark` class pre-mount, so the
  // visible flash is only the toggle icon flipping if the saved value
  // differs from the default.
  const [isDark, setIsDark] = useState(true);
  const [flipping, setFlipping] = useState(false);
  const btnRef = useRef<HTMLButtonElement>(null);

  // Reconcile state with localStorage AFTER mount — runs once, can't
  // affect SSR output, can't desync hydration.
  useEffect(() => {
    const saved = localStorage.getItem("theme");
    if (saved) setIsDark(saved === "dark");
  }, []);

  useEffect(() => {
    document.documentElement.classList.toggle("dark", isDark);
    localStorage.setItem("theme", isDark ? "dark" : "light");
  }, [isDark]);

  const toggle = () => {
    if (flipping) return;
    setFlipping(true);

    setTimeout(() => {
      const next = !isDark;
      setIsDark(next);
      document.documentElement.classList.toggle("dark", next);
    }, 300);

    setTimeout(() => setFlipping(false), 600);
  };

  return (
    <button
      ref={btnRef}
      onClick={toggle}
      aria-label="Toggle theme"
      className="group w-9 h-9 rounded-full border border-border/40 bg-card/60 backdrop-blur-sm flex items-center justify-center transition-all duration-300 hover:border-accent/50 hover:neon-glow-accent cursor-pointer"
      style={{
        perspective: "200px",
      }}
    >
      <span
        className="flex items-center justify-center"
        style={{
          transition: "transform 0.6s ease-in-out",
          transformStyle: "preserve-3d",
          transform: flipping ? "rotateY(180deg)" : "rotateY(0deg)",
        }}
      >
        {isDark ? (
          <Sun size={15} className="text-muted-foreground group-hover:text-accent transition-colors duration-300" />
        ) : (
          <Moon size={15} className="text-muted-foreground group-hover:text-accent transition-colors duration-300" />
        )}
      </span>
    </button>
  );
};

export default ThemeToggle;
